import { OptionComponent } from '../common/OptionComponent';
import { formatFieldOptionsToSelectOptions } from '../../utils/formatFieldOptionsToSelectOptions';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldOptions } from '../../hooks/useFieldOptions';
import { useLoadMoreFieldsHandler } from '../../hooks/useLoadMoreFieldsHandler';
import { useStrategy } from '../../hooks/useStrategy';
import DSFieldRecord from '../../filterQueryFormat/DSFieldRecord/DSFieldRecord';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UISelect from 'UIComponents/input/UISelect';
import Raven from 'raven-js';
import { virtualizedMenuRenderer } from 'foundations-components/transitional/utils/virtualizedMenuRenderer';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { jsx as _jsx } from "react/jsx-runtime";
const PropertySelect = ({
  filterFamily,
  disabled,
  inputUse,
  property,
  onChange,
  onPropertiesLoaded,
  readOnly,
  requiredPropertyName,
  hidePropertySelect
}) => {
  const {
    getField
  } = useStrategy();
  const {
    options: fieldOptions
  } = useFieldOptions(filterFamily, '');
  const [isLoadingFields, setIsLoadingFields] = useState(true);
  const loadMoreFields = useLoadMoreFieldsHandler();
  const prevFilterFamily = useRef('');
  useEffect(() => {
    if (prevFilterFamily.current !== filterFamily) {
      if (prevFilterFamily.current !== '') {
        onChange(undefined);
      }
      prevFilterFamily.current = filterFamily;
      setIsLoadingFields(true);
      loadMoreFields(filterFamily, 0, '').then(() => {
        onPropertiesLoaded === null || onPropertiesLoaded === void 0 || onPropertiesLoaded();
        setIsLoadingFields(false);
      }).catch(error => {
        Raven.captureException(error);
      });
    }
  }, [filterFamily, loadMoreFields, onChange, onPropertiesLoaded]);
  useEffect(() => {
    const propertyName = property ? property.name : undefined;
    if (!requiredPropertyName || requiredPropertyName === propertyName) {
      return;
    }
    const field = getField(filterFamily, requiredPropertyName);
    if (DSFieldRecord.isDSFieldRecord(field)) {
      onChange(field);
    } else {
      throw new Error('`requiredPropertyName` is invalid. Please pass in a valid property name.');
    }
  }, [filterFamily, getField, onChange, property, requiredPropertyName]);
  useEffect(() => {
    if (hidePropertySelect && !requiredPropertyName) {
      throw new Error('`requiredPropertyName` is required when hidePropertySelect is true.');
    }
  }, [hidePropertySelect, requiredPropertyName]);
  const formattedOptions = formatFieldOptionsToSelectOptions(fieldOptions, filterFamily);
  const onChangeLocal = evt => {
    const field = getField(filterFamily, evt.target.value);
    if (DSFieldRecord.isDSFieldRecord(field)) {
      onChange(field);
    }
  };
  const value = useMemo(() => {
    if (requiredPropertyName) {
      return requiredPropertyName;
    }
    return property ? property.name : undefined;
  }, [property, requiredPropertyName]);
  if (isLoadingFields) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.propertyFilter.loading"
      }),
      margin: "0",
      paddingBlock: "100",
      paddingInline: "200",
      showLabel: true,
      size: "sm"
    });
  }
  if (hidePropertySelect) {
    return null;
  }
  return /*#__PURE__*/_jsx(UISelect, {
    "data-test-id": "property-name-select",
    disabled: disabled,
    menuRenderer: virtualizedMenuRenderer,
    onChange: onChangeLocal,
    optionComponent: OptionComponent,
    options: formattedOptions,
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataFiltersUI.propertyFilter.propertySelect.placeholder"
    }),
    readOnly: readOnly || !disabled && !!requiredPropertyName,
    buttonUse: inputUse === 'on-dark' ? 'form-on-dark' : undefined,
    value: value
  });
};
export default PropertySelect;